document.addEventListener('DOMContentLoaded', function () {
  class MyElement extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({mode: 'open'});
    }

    connectedCallback() {
      this.render();
    }

    render() {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            padding: 10px;
            background-color: #f0f0f0;
            border: 1px solid #ddd;
          }
        </style>
        <div>
          <h2>My Custom Element</h2>
        </div>
      `;
    }
  }

  if (!window.customElements.get('my-element')) {
    customElements.define('my-element', MyElement);
  }

  // 创建 IntersectionObserver 实例
  window.YGImageLoader = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        const newImg = new Image();
        newImg.onload = () => {
          img.src = newImg.src;
          img.removeAttribute('data-src');  // 如果只需要加载一次
          img.removeAttribute('srcset');
          observer.unobserve(img);          // 停止观察该元素
        }
        newImg.src = img.getAttribute('data-src');
      }
    });
  }, {
    rootMargin: "50px", // 预加载距离视口 50px 的图片
  });

  class YGImage extends HTMLElement {
    constructor() {
      super()
      this.init()
    }

    get images() {
      return this.querySelectorAll('img[data-src]')
    }

    init() {
      // 这里放置初始化逻辑
      this.loaderImg()
    }

    loaderImg() {
      // 这里放置自定义逻辑
      this.images.forEach((img) => {
        window.YGImageLoader.observe(img)
      })
    }
  }

  if (!window.customElements.get('yg-image')) {
    window.customElements.define('yg-image', YGImage)
  }

  const onYouTubePromise = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => resolve();
  });

  class YGDeferredMedia extends HTMLElement {
    constructor() {
      super();
    }

    static get observedAttributes() {
      return ['playing'];
    }

    connectedCallback() {
      this.bindPosterButton();

      this.addEventListener('click', (e) => {
        const video = this.querySelector('video')
        if (
          video &&
          e.target === video &&
          !video.hasAttribute('controls')
        ) {
          e.preventDefault()
          e.stopPropagation()
          if (this.playing) {
            this.pause()
          } else {
            this.play()
          }
        }
      });

      if (this.autoplay) {
        this.inViewObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
            }
          });
        });
        this.inViewObserver.observe(this);
      }
    }

    get posterElement() {
      return this.querySelector('[id^="YG-DeferredPoster-"]');
    }

    get autoplay() {
      return this.hasAttribute('autoplay');
    }

    get playing() {
      return this.hasAttribute('playing');
    }

    get player() {
      return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this.playerHandler(target, prop);
          };
        }
      });
    }

    bindPosterButton() {
      const poster = this.posterElement;
      if (poster && !poster.hasListener) {
        poster.addEventListener('click', this.onPosterClick.bind(this));
        poster.hasListener = true;
      }
    }

    async playerTarget() {
      const template = this.querySelector('template');
      if (!template) return Promise.resolve(null);

      if (!this.mediaElement) {
        const fragment = template.content.cloneNode(true);
        this.appendChild(fragment);
        this.mediaElement = this.querySelector('video, iframe');
      }

      this.bindPosterButton();
      return Promise.resolve(this.mediaElement);
    }

    playerHandler(target, prop) {
      if (!target) return;

      if (prop === 'play' && typeof target.play === 'function') {
        target.play();
        this.setAttribute('playing', '');
        this.removeAttribute('paused');
      }

      if (prop === 'pause' && typeof target.pause === 'function') {
        target.pause();
        this.removeAttribute('playing');
        this.setAttribute('paused', '');
      }
    }

    onPosterClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.playing) {
        this.pause();
      } else {
        this.play();
      }
    }

    init() {
      if (!this.paused) {
        this.play();
      }
      return () => this.pause();
    }

    play() {
      if (!this.playing) {
        this.player.play();
      }
    }

    pause() {
      if (this.playing) {
        this.player.pause();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'playing') {
        if (oldValue === null && newValue === '') {
          this.dispatchEvent(new CustomEvent('video:play', {bubbles: true}));
          this.removeAttribute('paused');
        }
        if (newValue === null) {
          this.dispatchEvent(new CustomEvent('video:pause', {bubbles: true}));
          this.setAttribute('paused', '');
        }
      }
    }
  }

  class YGMedia extends YGDeferredMedia {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback(); // 保留父类逻辑

      if (!this.querySelector('img') && !this.hasAttribute('autoplay')) {
        this.inViewObserverNoAutoplay = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadMediaOnly();
              this.inViewObserverNoAutoplay.disconnect();
            }
          });
        });
        this.inViewObserverNoAutoplay.observe(this);
      }
    }

    async loadMediaOnly() {
      if (!this.mediaElement) {
        const template = this.querySelector('template');
        if (template) {
          const fragment = template.content.cloneNode(true);
          this.appendChild(fragment);
          this.mediaElement = this.querySelector('video, iframe');
          this.setAttribute('loaded', '');
          this.closest('.media')?.classList.remove('loading');
          this.bindPosterButton();
        }
      }
    }

    async playerTarget() {
      if (this.hasAttribute('host')) {
        this.setAttribute('loaded', '');
        this.closest('.media')?.classList.remove('loading');

        return new Promise(async (resolve) => {
          const templateElement = this.querySelector('template');
          if (templateElement) {
            templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
          }
          this.bindPosterButton();

          const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('(max-width: 1023px)').matches;
          const host = this.getAttribute('host');

          if (host === 'youtube') {
            if (!window.YT || !window.YT.Player) {
              await new Promise(res => {
                const script = document.createElement('script');
                script.src = 'https://www.youtube.com/iframe_api';
                script.onload = res;
                document.head.appendChild(script);
              });
              await new Promise(res => {
                window.onYouTubeIframeAPIReady = res;
              });
            }

            const player = new YT.Player(this.querySelector('iframe'), {
              events: {
                onReady: () => {
                  if (muteVideo) player.mute();
                  resolve(player);
                },
                onStateChange: (event) => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute('playing', '');
                    this.removeAttribute('paused');
                  }
                  if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
                    this.removeAttribute('playing');
                    this.setAttribute('paused', '');
                  }
                }
              }
            });
          }

          if (host === 'vimeo') {
            if (!window.Vimeo || !window.Vimeo.Player) {
              await new Promise(res => {
                const script = document.createElement('script');
                script.src = 'https://player.vimeo.com/api/player.js';
                script.onload = res;
                document.head.appendChild(script);
              });
            }

            const player = new Vimeo.Player(this.querySelector('iframe'));
            if (muteVideo) await player.setMuted(true);

            player.on('play', () => {
              this.setAttribute('playing', '');
              this.removeAttribute('paused');
            });
            player.on('pause', () => {
              this.removeAttribute('playing');
              this.setAttribute('paused', '');
            });
            player.on('ended', () => {
              this.removeAttribute('playing');
              this.setAttribute('paused', '');
            });

            resolve(player);
          }

          if (host === 'pimmedia') {
            // 假设 pimmedia 的 iframe 已经在页面上
            const iframe = this.querySelector('iframe');
            console.log("第三季我")
            if (!iframe) {
              console.warn('pimmedia iframe not found');
              return;
            }

            // 假设 mute 需要 postMessage 或类似接口，这里根据你具体接口改写
            if (muteVideo) {
              // 这里看你的 pimmedia 播放器怎么静音，比如 postMessage
              iframe.contentWindow.postMessage({action: 'mute'}, '*');
            }

            // 监听 pimmedia 的消息事件（例如播放、暂停、结束）
            window.addEventListener('message', (event) => {
              // 确认是 pimmedia 的消息，可根据 event.origin 做验证
              const data = event.data;

              if (typeof data !== 'object' || !data.event) return;

              if (data.event === 'play') {
                this.setAttribute('playing', '');
                this.removeAttribute('paused');
              }
              if (data.event === 'pause' || data.event === 'ended') {
                this.removeAttribute('playing');
                this.setAttribute('paused', '');
              }
            });

            // 返回一个模拟的 player 对象给 resolve
            resolve({
              play() {
                iframe.contentWindow.postMessage({action: 'play'}, '*');
              },
              pause() {
                iframe.contentWindow.postMessage({action: 'pause'}, '*');
              },
              mute() {
                iframe.contentWindow.postMessage({action: 'mute'}, '*');
              }
            });
          }
        });
      } else {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        this.setAttribute('loaded', '');
        this.closest('.media')?.classList.remove('loading');

        const player = this.querySelector('video');
        player.addEventListener('play', () => {
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
          this.removeAttribute('suspended');
        });
        player.addEventListener('pause', () => {
          if (!player.seeking && player.paused) {
            this.removeAttribute('playing');
            this.setAttribute('paused', '');
          }
        });

        this.bindPosterButton();
        return player;
      }
    }

    playerHandler(target, prop) {
      const host = this.getAttribute('host');

      if (host === 'youtube') {
        if (prop === 'play') {
          target.playVideo();
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
        } else {
          target.pauseVideo();
          this.removeAttribute('playing');
          this.setAttribute('paused', '');
        }
      } else if (host === 'vimeo') {
        if (prop === 'play') {
          target.play();
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
        } else {
          target.pause();
          this.removeAttribute('playing');
          this.setAttribute('paused', '');
        }
      } else {
        if (prop === 'play') {
          target.play().catch((error) => {
            if (error.name === 'NotAllowedError') {
              this.setAttribute('suspended', '');
              target.controls = true;
              const replacementImageSrc = target.previousElementSibling?.currentSrc;
              if (replacementImageSrc) {
                target.poster = replacementImageSrc;
              }
            }
          });
          this.setAttribute('playing', '');
          this.removeAttribute('paused');
        } else {
          target.pause();
          this.removeAttribute('playing');
          this.setAttribute('paused', '');
        }
      }
    }
  }

  if (!customElements.get('yg-video')) {
    customElements.define('yg-video', YGMedia);
  }

  class YGSwiper extends HTMLElement {
    constructor() {
      super()
    }
    connectedCallback() {
      this.getAllAttributes()
      this.initSwiper()
    }

    getAllAttributes() {
      let booleanAttrs = ['loop', 'pagination', 'navigation'];
      let jsonAttrs = ['breakpoints']
      for (const attr of this.attributes) {
        if (attr.name.startsWith('data-')) {
          const cleanName = attr.name.slice(5); // 去除 'data-' 前缀

          let value = attr.value;
          if (booleanAttrs.includes(cleanName)) {
            value = value === 'true';
          } else if (jsonAttrs.includes(cleanName)) {
            value = this.parseSwiperBreakpoints(value);
          }

          this[cleanName] = value;
        }
      }
    }

    parseSwiperBreakpoints(str) {
      const result = {};
      const pairs = str.split(',');

      for (const pair of pairs) {
        const [breakpoint, values] = pair.split(':');
        if (!breakpoint || !values) continue;

        const [slides, space] = values.split('-').map(Number);

        result[breakpoint.trim()] = {
          slidesPerView: slides,
          spaceBetween: space
        };
      }

      return result;
    }

    initSwiper() {
      const swiper = new Swiper(this, {
        // 这里放置 swiper 的配置
        loop: this['loop'],
        autoplay: this['autoplay'] ? {
          delay: this['autoplay'],
          stopOnLastSlide: false,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        } : false,
        pagination: this['pagination'] ? {
          el: '.swiper-pagination',
          clickable: true,
        } : false,
        navigation: this['navigation'] ? {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        } : false,
        breakpoints: this['breakpoints'] ? this['breakpoints'] : false
      })
    }
  }
  if (!window.customElements.get('yg-swiper')) {
    window.customElements.define('yg-swiper', YGSwiper)
  }

  class YGSlide extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      this.initSwiper()
    }

    initSwiper() {
      new Swiper(this, {
        pagination: {
          el: '.yg-slide-pagination',
          // 数字指示器
          // type: 'fraction',
          clickable: true,
        },
        navigation: {
          nextEl: this.querySelector('.yg-slide-next'),
          prevEl: this.querySelector('.yg-slide-prev'),
        },
        breakpoints: {
          220: {
            slidesPerView: 1.4,
            spaceBetween: 16,
          },
          700: {
            slidesPerView: 2,
            spaceBetween: 16,
          },
          1150: {
            slidesPerView: 3,
            spaceBetween: 24,
          },
        }
      })
    }
  }

  if (!window.customElements.get('yg-slide')) {
    window.customElements.define('yg-slide', YGSlide)
  }

  class ScrollingImages extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('mouseover', this._pauseScrolling.bind(this));
      this.addEventListener('mouseout', this._resumeScrolling.bind(this));

      this.resizeObserver = new ResizeObserver((entries) => {
        this._calculateDuration(entries);
        this._duplicateIfNeeded();
      });
    }

    connectedCallback() {
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
    }

    _calculateDuration(entries) {
      const scrollingSpeed = parseInt(this.getAttribute('scrolling-speed') || 20),
        contentWidth = entries[0].contentRect.width,
        slowFactor = 1 + (Math.min(15000, contentWidth) - 375) / (1200 - 375);

      const contentDiv = this.querySelector('img');
      if (contentDiv) {
        this.style.setProperty(
          '--marquee-animation-duration',
          `${((scrollingSpeed * slowFactor * contentDiv.clientWidth) / contentWidth).toFixed(3)}s`
        );
      }
    }

    _pauseScrolling() {
      this.parentNode.querySelectorAll('scrolling-image').forEach((item) => {
        item.style.animationPlayState = 'paused';
      });
    }

    _resumeScrolling() {
      this.parentNode.querySelectorAll('scrolling-image').forEach((item) => {
        item.style.animationPlayState = 'running';
      });
    }

    _duplicateIfNeeded() {
      const container = this;
      const images = container.querySelectorAll('yg-image');
      let totalWidth = 0;

      images.forEach((img) => {
        totalWidth += img.clientWidth;
      });

      if (totalWidth < container.clientWidth) {
        const cloneNeeded = images.length < 6;
        if (cloneNeeded) {
          images.forEach((img) => {
            const clone = img.cloneNode(true);
            container.appendChild(clone);
          });
        }
      }
    }
  }
  if (!window.customElements.get('scrolling-image')) {
    window.customElements.define('scrolling-image', ScrollingImages);
  }

  class YGVideoList extends HTMLElement {
    constructor() {
      super()
      window.addEventListener('resize',()=>{
        // 在窗口大小发生变化时执行的操作
        this.initStyle()
        this.toggleVideo()
      })

      window.dispatchEvent(new Event('resize'))
    }
    initStyle() {
      let windowWidth = window.innerWidth;

      if(windowWidth > 700){
        let videoHeight = this.querySelector('.yg-main-video').getBoundingClientRect().height
        this.querySelector('.yg-video-list').style.height = `${videoHeight}px`
      }else{
        let more = this.querySelector(".yg-more")
        let less = this.querySelector(".yg-less")
        let videoListBox = this.querySelector('.yg-video-list')
        let videoListLength = this.querySelectorAll('.yg-video-block__image').length
        let videoHeight = this.querySelector('.yg-video-block__image').getBoundingClientRect().height
        let showLength = 3

        videoListBox.style.height = `${showLength * videoHeight + (showLength - 1) * 20}px`

        more.addEventListener("click",()=>{
          showLength += 3
          let show = showLength >= videoListLength ? videoListLength:showLength

          videoListBox.style.height = `${show * videoHeight + (show - 1) * 20}px`
          if(show == videoListLength) {
            more.style.display = 'none'
            less.style.display = 'flex'
          }
        })

        less.addEventListener('click', () => {
          showLength = 3
          videoListBox.style.height = `${showLength * videoHeight + (showLength - 1) * 20}px`

          less.style.display = 'none'
          more.style.display = 'flex'
        })
      }
    }
    toggleVideo() {
      let videoList = this.querySelectorAll('.yg-video-block__image')
      let mainVideo = this.querySelector('.yg-main-video')
      let dialog = this.querySelector('.yg-video-block__dialog')
      let windowWidth = window.innerWidth;


      videoList.forEach(function(video){
        video.addEventListener("click",function(e){
          if(e.target.classList.contains('yg-video-block__open')){
            let template = video.querySelector('.yg-video-block__template')
            if (windowWidth > 767) {
              mainVideo.innerHTML = template.innerHTML
            }else{
              dialog.innerHTML = template.innerHTML
              document.querySelector('body').style.overflowY = 'hidden'
              dialog.showModal()
            }
            videoList.forEach(function(video){
              video.classList.remove('active')
            })
            video.classList.add('active')
          }
        })
      })

      dialog.addEventListener("click",function(e){
        if(e.target.classList.contains('yg-video-block__dialog')){
          document.querySelector('body').style.overflowY = 'auto'
          videoList.forEach(function(video){
            video.classList.remove('active')
          })
          dialog.close()
        }
      })
    }
  }

  if (!window.customElements.get('yg-video-list')) {
    window.customElements.define('yg-video-list',YGVideoList)
  }

  class YGToggle extends HTMLElement {
    constructor() {
      super();
    }

    updateContentHeight() {
      const scrollHeight = this.content.scrollHeight;
      this.content.style.maxHeight = scrollHeight + 'px';
    }

    connectedCallback() {

      this.button = this.querySelector('.yg-toggle-button');
      this.content = this.querySelector('.yg-toggle-content');
      if (this.button && this.content) {
        this.button.addEventListener('click', () => {
          const isActive = this.button.classList.contains("active");
          this.button.classList.toggle('active', !isActive);

          console.log(this.button)
          this.content.classList.toggle('open', !isActive);
          if(!isActive){
            const scrollHeight = this.content.scrollHeight;
            this.content.style.maxHeight = scrollHeight + 'px';
          }
          else{
            this.content.style.maxHeight = '0px';
          }
        });
        if (this.content.classList.contains('open')) {
          this.content.style.maxHeight = this.content.scrollHeight + 'px';
        }
      }

      window.addEventListener('resize', () => {
        if (this.content.classList.contains('open')) {
          this.updateContentHeight();
        }
      });
    }

  }

  if (!customElements.get('yg-toggle')) {
    customElements.define('yg-toggle', YGToggle);
  }
});

  